import {
    UserResult,
    SessionData,
    PageLoad,
    PageAdvance,
    UserAnswer,
    BidsCollected,
    AdsLoaded,
    TaboolaSpend,
    Consent,
    AmazonBids,
    HasOffersIneligible,
    Uci
} from "./tracking_events";
import { getTimestampInISO, trackPageFocusTime, getCookie, removeCookie } from "../functions";
import { update, getMany, delMany } from 'idb-keyval';
import {send} from "../transport";
import {sendDataToKafka} from "../kafka";

const map = {
    'page_load': PageLoad,
    'session_start': SessionData,
    'user_result': UserResult,
    'page_advance': PageAdvance,
    'user_answer': UserAnswer,
    'bids_collected': BidsCollected,
    'ads_loaded': AdsLoaded,
    'taboola_spend': TaboolaSpend,
    'consent': Consent,
    'amazonbids': AmazonBids,
    'ho_conv_ineligible': HasOffersIneligible,
    'uci': Uci
}
const queued_events_key = 'queued_events';
const user_answered_key = 'user_answered';
const answer_previous_pageload_key = 'answer_previous_pageload';
const taboola_spend_key = 'taboola_spend';

let keys = [queued_events_key, user_answered_key, answer_previous_pageload_key, taboola_spend_key];

trackPageFocusTime();

const eventsWithPreviousPageLoad = [UserAnswer.type];

const getServerEvents = () => {
    const tags = document.querySelectorAll('meta[name^="evt:"]');
    const configs = Array.from(tags).map((tag) => {
        const data = atob(tag.attributes.content.value);
        return {
            name: tag.attributes.name.value.split('evt:').pop(),
            data: JSON.parse(data),
        }
    });

    tags.forEach((t) => t.remove()); // cleanup
    return configs.map((c) => map[c.name].fromServerParameters(c.data));
}

const attachPageAdvanceListeners = () => {
    const uuid = document.querySelector('meta[name="uuid"]').attributes.content.value;

    const nodes = document.querySelectorAll('[data-tpa]');
    nodes.forEach((n) => {
        n.addEventListener('click', (e) => {
            update(queued_events_key, (v = []) => {
                if (!v) { v = []; }
                v.push(PageAdvance.fromServerParameters({ uuid, source: n.dataset.tpa }));
                return v;
            });
        });
    });
}

const question_answer_key = 'q_answer';
const attachUserAnswerListener = () => {
    const answerNodes = document.querySelectorAll('[data-tpa=answer]');

    answerNodes.forEach((n) => {
        if (n.type === 'submit') {
            n.closest('form').addEventListener('submit', (e) => {
                e.preventDefault();
                const actionUrlChunks = e.target.action.split('/');
                const userAnswer = [
                    actionUrlChunks[actionUrlChunks.length - 2], // question token
                    null, // answer token
                    document.querySelector('input[name=answer]').value.trim(), // answer-input placeholder
                    getTimestampInISO(),
                    Date.now(),
                ];

                localStorage.setItem(question_answer_key,  `/${userAnswer[0]}/${userAnswer[1]}`);
                update(user_answered_key, (v) => userAnswer);
                e.target.submit();
            });
        } else {
            n.addEventListener('click', (e) => {
                const urlChunks = !!e.target.getAttribute('href')
                    ? e.target.getAttribute('href').split('/')
                    : e.target.closest('[data-tpa=answer]').getAttribute('href').split('/');
                const userAnswer = [
                    urlChunks[urlChunks.length - 2], // question token
                    urlChunks[urlChunks.length - 1], // answer token
                    null, // answer-input placeholder
                    getTimestampInISO(),
                    Date.now(),
                ];

                localStorage.setItem(question_answer_key,  `/${userAnswer[0]}/${userAnswer[1]}`);
                update(user_answered_key, (v) => userAnswer);
            });
        }
    });
}

const attachBidsListener = (server) => {
    const isQuestionPage = server.data[6] === 'question';
    const preloaded = {
        'pageloadTimestamp': server.data[0],
        'pageloadTimestampMs': parseInt(server.data[1]),
        'pageloadId': server.data[2],
        'sessionId': server.data[3],
        'offerUrlId': server.data[4],
        'userCountry': server.data[5],
        'content_token': server.data[6] ?? null,
        'page_type': server.data[7],
        'question_token': isQuestionPage && server.data[8] ? server.data[8] : null,
    };

    document.addEventListener(BidsCollected.type, (e) => {
        if (!!e.detail && !!e.detail.bids) {
            e.detail.bids.forEach((bid) => {
                let _bid = Object.assign(bid, preloaded);

                sendDataToKafka([{
                    type: BidsCollected.type,
                    data: [_bid]
                }]);

                delete _bid.pageloadTimestampMs;
                delete _bid.content_token;
                delete _bid.page_type;
                delete _bid.question_token;

                send([{
                    type: BidsCollected.type,
                    plain_data: true,
                    data: [_bid]
                }]);
            });
        }
    });
}

const attachAmazonBidsListener = (server) => {
    document.addEventListener(AmazonBids.type, ({ detail: { bids } }) => {
        bids.forEach(bid => {
            let timestampOffset = (parseInt(bid.ad_loaded_timestamp) - parseInt(bid.client_timestamp)) + parseInt(server.data[0]);
            let _data = {
                timestamp: timestampOffset,
                pageload_id: server.data[1],
                offer_url_id: server.data[2],
                ab_test: null,
                ext_id: server.data[3],
                session_id: server.data[4],
                country: server.data[5],
                ad_unit: bid.ad_unit,
                amznbid: bid.amznbid,
                amznsz: bid.amznsz
            }

            sendDataToKafka([{ type: AmazonBids.type, data: [_data] }]);
        });
    });
}

const trackPageDepth = () => {
    const keyPd = 'pd';
    const keyPdTTL = 'pdTTL';
    const nowDate = new Date();
    const isPageDepthExpired = (localStorage.getItem(keyPdTTL) && (new Date(localStorage.getItem(keyPdTTL)).getTime() < nowDate.getTime()));

    let valPd = localStorage.getItem(keyPd) ?? null;

    if (!localStorage.getItem(keyPdTTL) || isPageDepthExpired) {
        nowDate.setDate(nowDate.getDate() + 1);
        localStorage.setItem(keyPdTTL, nowDate.toUTCString());
        valPd = null;
    }

    if (valPd) {
        localStorage.setItem(keyPd, (parseInt(valPd) + 1).toString());
    } else {
        localStorage.setItem(keyPd, '1');
    }
}

const attachAdsLoadingListener = () => {
    let clientTimestamp = Math.floor(Date.now() / 1000);
    let clientTimestampInMs = Date.now();
    let pageloadEvent = document.querySelector('meta[name^="evt:page_load"]');
    if(!pageloadEvent) return;

    let data = JSON.parse(atob(pageloadEvent.attributes.content.value));
    let serverTimestamp = data.server_time;
    let serverTimestampInMs = data.server_time_in_ms;
    let pageloadId = data.uuid;
    let adsCounter = {} // ad refresh counter per slot;

    if (typeof googletag !== 'undefined') {
        googletag.cmd.push(function () {
            googletag.pubads().addEventListener('slotResponseReceived', function (event) {
                let slot = event.slot;
                let adLoadedTimestamp = Math.floor(Date.now() / 1000);
                let adLoadedTimestampInMs = Date.now();
                let formulaResult = (adLoadedTimestamp - clientTimestamp) + (clientTimestamp - (clientTimestamp - serverTimestamp));
                let formulaResultInMs = (adLoadedTimestampInMs - clientTimestampInMs) + (clientTimestampInMs - (clientTimestampInMs - serverTimestampInMs));

                let advertiserId = null;
                let lineItemId = null;
                let slotKey = slot.getAdUnitPath().split('/')[slot.getAdUnitPath().split('/').length - 1];

                if (!!slot.getResponseInformation()) {
                    advertiserId = slot.getResponseInformation().advertiserId;
                    lineItemId = slot.getResponseInformation().lineItemId;
                }

                if (!slot.getOutOfPage()) {
                    adsCounter = countAdResponse(adsCounter, slotKey);
                }

                let slotDataToSend = {
                    pageload_id: pageloadId,
                    ad_unit: slot.getAdUnitPath(),
                    timestamp: formulaResult,
                    timestamp_ms: formulaResultInMs,
                    refresh_counter: (!slot.getOutOfPage() && adsCounter.hasOwnProperty(slotKey)) ? adsCounter[slotKey] : 0,
                    advertiser_id: advertiserId,
                    line_item_id: lineItemId
                };

                sendDataToKafka([{
                    type: AdsLoaded.type,
                    data: [slotDataToSend]
                }]);

                delete slotDataToSend.timestamp_ms;
                delete slotDataToSend.refresh_counter;
                delete slotDataToSend.advertiser_id;
                delete slotDataToSend.line_item_id;

                send([{
                    type: AdsLoaded.type,
                    data: [slotDataToSend]
                }]);
            });
        });
    }
}

function countAdResponse(counter, slot) {
    if (!counter.hasOwnProperty(slot)) {
        counter[slot] = 0;
    } else {
        counter[slot] = parseInt(counter[slot]) + 1;
    }

    return counter;
}

const enrichDataLayer = () => {
    if (typeof dataLayer === 'object') {
        dataLayer.forEach((item) => {
            if (item.ob_unit == null && !!document.querySelector('.OUTBRAIN')) {
                item.ob_unit = document.querySelector('.OUTBRAIN').getAttribute('data-widget-id');
            }

            if (item.ad_sum === 0 && !!localStorage.key('adSum')) {
                item.ad_sum = parseFloat(localStorage.getItem('adSum'));
            }
        });
    }

    // track ad_sticky_rendered on QFS question page
    if (typeof window.googletag === 'object' && typeof window.googletag.cmd === 'object'
        && location.host.includes('quiz-facts.com') && location.pathname.includes('/question')
    ) {
        googletag.cmd.push(function () {
            googletag.pubads().addEventListener('slotRenderEnded', function (e) {
                if (e.slot.getAdUnitPath().includes('sticky') && typeof window.dataLayer === 'object') {
                    dataLayer.push({
                        event: 'ad_sticky_rendered',
                        status: Number(!e.isEmpty)
                    });
                }
            });
        });
    }
}

const attachConsentListener = () => {
    let consentEvent = document.querySelector('meta[name^="evt:consent"]');
    if(!consentEvent) return;

    let data = JSON.parse(atob(consentEvent.attributes.content.value));
    let pageloadId = data.uuid;

    // consent events tracking
    if (typeof window.__tcfapi !== 'undefined') {
        window.__tcfapi('addEventListener', 2, function (tcData, success) {
            if (success) {
                if (['loaded'].includes(tcData.cmpStatus)) {
                    let initiated = !!window.__cmpInit;
                    if (!initiated) {
                        window.__cmpInit = true;
                        sendDataToKafka([{
                            type: 'consent',
                            data: ['cmp_initiated', pageloadId, Date.now(), null]
                        }]);
                    }
                }

                if (['cmpuishown'].includes(tcData.eventStatus)) {
                    sendDataToKafka([{
                        type: 'consent',
                        data: ['cmp_dialog_loaded', pageloadId, Date.now(), null]
                    }]);
                }

                if (['useractioncomplete', 'tcloaded'].includes(tcData.eventStatus)) {
                    const eventTypeName =  (tcData.eventStatus === 'useractioncomplete') ? 'cmp_consent_given' : 'cmp_consent_preexists';

                    let consentValues = [];
                    if (tcData.purpose.consents && typeof tcData.purpose.consents === 'object') {
                        consentValues = consentValues.concat(Object.values(tcData.purpose.consents));
                    }

                    if (tcData.specialFeatureOptins && typeof tcData.specialFeatureOptins === 'object') {
                        consentValues = consentValues.concat(Object.values(tcData.specialFeatureOptins));
                    }

                    for (let index in consentValues) {
                        if (consentValues[index] === true) {
                            sendDataToKafka([{
                                type: 'consent',
                                data: [eventTypeName, pageloadId, Date.now(), null]
                            }]);
                            break;
                        }
                    }
                }
            }
        });
    }

    // ads request tracking as a part of consent queue
    if (typeof googletag !== 'undefined') {
        googletag.cmd.push(function () {
            googletag.pubads().addEventListener("slotRequested", (event) => {
                if (!!event.slot) {
                    sendDataToKafka([{
                        type: 'consent',
                        data: ['ad_request', pageloadId, Date.now(), event.slot.getAdUnitPath()]
                    }]);
                }
            });
        });
    }
}

const attachHasOffersIneligible = () => {
    const _event = HasOffersIneligible.type;
    let _hoIneligibleMeta = document.querySelector('meta[name^="evt:ho_conv_ineligible"]');
    let _hoIneligible = getCookie(_event);
    if (!_hoIneligible || !_hoIneligibleMeta) return;

    let _hoIneligibleData = _hoIneligible.split(':');
    let _timestamp = _hoIneligibleData.length > 0 ? _hoIneligibleData[0] : null;
    let _transaction = (_hoIneligibleData.length > 1 && !!_hoIneligibleData[1].length) ? _hoIneligibleData[1] : null;
    let _reason = _hoIneligibleData.length > 2 ? _hoIneligibleData[2] : null;
    let _details = (_hoIneligibleData.length > 3 && !!_hoIneligibleData[3].length) ? _hoIneligibleData[3] : null;
    let _uciUserHash = (_hoIneligibleData.length > 4 && !!_hoIneligibleData[4].length) ? _hoIneligibleData[4] : null;

    let _metadata = JSON.parse(atob(_hoIneligibleMeta.attributes.content.value));

    sendDataToKafka([{
        type: _event,
        data: [parseInt(_timestamp), _metadata.session_id, _transaction, _reason, _details, _metadata.uuid, _uciUserHash]
    }]);

    removeCookie(_event);
}

const attachUci = () => {
    const event = Uci.type;
    let uciMetadata = document.querySelector('meta[name^="evt:uci"]');
    let uciTrackingEventCookie = getCookie(event);

    if (!uciMetadata || !uciTrackingEventCookie) {
        return;
    }

    const [uciUserHash, transactionId] = uciTrackingEventCookie.split(':');

    let metadata = JSON.parse(atob(uciMetadata.attributes.content.value));

    sendDataToKafka([{
        type: event,
        data: [metadata.uuid, transactionId, uciUserHash, metadata.ip, metadata.user_agent, metadata.content_token, metadata.browser_language]
    }]);

    removeCookie(event);
}

// event listeners
attachPageAdvanceListeners();
attachUserAnswerListener();
trackPageDepth();
enrichDataLayer();
attachAdsLoadingListener();
attachConsentListener();
attachHasOffersIneligible();
attachUci();

getMany(keys)
    .then(([events = [], userAnswered, answerPreviousPageLoad]) => {
        getServerEvents().forEach((e) => {
            if (!eventsWithPreviousPageLoad.includes(e.type)) {
                events.push(e);
            }

            // user_answer
            if (e.type === UserAnswer.type) {
                if (!!userAnswered && !!answerPreviousPageLoad) {
                    answerPreviousPageLoad.data.push(...userAnswered);
                    events.push(answerPreviousPageLoad);
                }

                update(answer_previous_pageload_key, (v) => e);
                keys = keys.filter((v) => v !== answer_previous_pageload_key);
            }

            // bids_collected
            if (e.type === BidsCollected.type) {
                attachBidsListener(e);
            }

            // amazonbids
            if (e.type === AmazonBids.type) {
                attachAmazonBidsListener(e);
            }
        });

        events = events.filter((e) => ![AdsLoaded.type, BidsCollected.type, Consent.type, AmazonBids.type, HasOffersIneligible.type, Uci.type].includes(e.type));
        events.map((e) => {
            e.data = e.data.map(item => (e.type !== PageAdvance.type && !item) ? '' : item);
            return e;
        });

        sendDataToKafka(events);

        // adjust data for RMQ
        events.forEach(function (event) {
            // taboola_spend
            if (event.type === TaboolaSpend.type) {
                let _data = {
                    'server_timestamp': event.data[0],
                    'session_id': event.data[1],
                    'partner_encrypt': event.data[2],
                    'country': event.data[3],
                    'offer_url_id': event.data[4],
                    'ext_id': event.data[5],
                    'device_type': event.data[6],
                }
                event.data = _data;
            }

            // page_load
            if (event.type === PageLoad.type) {
                event.data.splice(7, 2);
                if (typeof event.data[6] == 'string') {
                    event.data[6] = 0;
                }
            }

            // page_advance
            if (event.type === PageAdvance.type) {
                event.data.splice(2, 1);
            }

            // user_result
            if (event.type === UserResult.type) {
                event.data.splice(1, 1);
            }

            // user_answer
            if (event.type === UserAnswer.type) {
                event.data.splice(2, 2);
                event.data.splice(event.data.length - 1, 1);
            }
        });
        send(events);
    })
    .then(() => {
        delMany(keys);
    });